import React, { FC, memo, useEffect, useMemo, useState } from 'react';
import styles from './CrossSell.module.scss';
import '@animxyz/core';
import Icon from '@ant-design/icons/lib/components/Icon';
import LeftSvg from '../../public/icon/LeftCurrentColor.svg';
import Image from 'next/image';
import LinkButton from '../LinkButton';
import { handleRichText } from '@/utils/handleRichText';
import { Swiper, SwiperSlide } from 'swiper/react';
import classNames from 'classnames';
import { useRouter } from 'next/router';

type Props = {
  bannerData?: any;
  className?: any;
  data: any;
};

type Swiper = any;

const productCardGAMap: any = {
  'MyMillion Product Card': {
    productName: 'Mymillion',
    productId: 'MMP'
  },
  'vPrime Product Card': {
    productName: 'vPrime',
    productId: 'VPrime'
  },
  'MyCover Product Card': {
    productName: 'MyCover',
    productId: 'BIGT'
  },
  'MyTerm Plus Product Card': {
    productName: 'MyTermPlus',
    productId: 'MTR'
  },
  'EasyWealthPlus Product Card': {
    productName: 'EasyWealthPlus',
    productId: 'EasyWealthPlus'
  },
  'MyTravel Product Card': {
    productName: 'MyTravel',
    productId: 'MyTravel'
  },
  'Motor Product Card': {
    productName: 'Motor',
    productId: 'Motor'
  },
  'Home Product Card': {
    productName: 'Home',
    productId: 'Home'
  },
  'Helper Product Card': {
    productName: 'Helper',
    productId: 'Helper'
  },
  'Pet Product Card': {
    productName: 'Pet',
    productId: 'Pet'
  }
};

const CrossSell: FC<Props> = memo(props => {
  const { cross_sell_title, icon, introduction, selling_points, remark, product_block_type } =
    props.data;
  const product_cards = props?.data?.product_cards?.filter((item: { product_name: any }) => {
    if (item.product_name) {
      return item;
    }
  });

  const [isSwiperProductCards, setSwiperProductCards] = useState(false);
  const [swiperProductList, setSwiperProductList] = useState([]);
  const [swiper, setSwiper] = useState<Swiper>();
  const [activeSwiperIndex, setActiveSwiperIndex] = useState(0);
  const router = useRouter();

  const handleGA = (productTitle: any) => {
    const targetProduct = productCardGAMap[productTitle] || {};
    window?.dataLayer?.push({
      event: 'productcardClick',
      ecommerce: {
        click: {
          actionField: { list: 'Blog Article' },
          products: [
            {
              id: targetProduct.productId,
              name: targetProduct.productName,
              category: router.query.category,
              brand: 'FWD'
            }
          ]
        }
      }
    });
  };

  const newProductList = useMemo(() => {
    let curIndex = 0;
    let curItem = 1;
    const productList = product_cards?.reduce((prev: any, cur: any) => {
      if (curItem === 1) {
        prev[curIndex] = [cur];
        curItem++;
        return prev;
      } else {
        prev[curIndex] = [...prev[curIndex], cur];
        curItem = 1;
        curIndex++;
        return prev;
      }
    }, []);
    return productList;
  }, []);

  const RemarkBlock = () => {
    const [open, setOpen] = useState(false);
    return (
      <div className={styles.remark_block}>
        <div className={styles.remark_title_block} onClick={() => setOpen(!open)}>
          <span className={styles.remark_title}>備註</span>
          <Icon
            component={LeftSvg}
            className={open ? styles.collapse_icon_up : styles.collapse_icon_down}
          />
        </div>
        {open && (
          <div
            className={styles.remark_description}
            id={'rich_text_wrapper'}
            dangerouslySetInnerHTML={{
              __html: handleRichText(remark)
            }}
          ></div>
        )}
      </div>
    );
  };

  const ProductCard = (card: {
    product_type: string;
    title: string;
    product_type_icon: { url: string };
    product_name: string;
    product_card_icon_desktop: { url: string };
    product_description: string;
    highlight: string;
    price: string;
    price_unit: string;
    action_link: { title: string; url: string };
    product_brochure_link: {
      icon: { url: string };
      title: string;
      url: string;
    };
  }) => {
    return (
      <div className={styles.product_card} key={card?.product_name}>
        <div className={styles.head_block}>
          <div className={styles.head_left_block}>
            <div className={styles.tab_block}>
              <Image src={card?.product_type_icon?.url} alt={''} width={20} height={18} />
              <div className={styles.tab_title}>{card?.product_type}</div>
            </div>
            <div className={styles.title}>{card?.product_name}</div>
          </div>
          <Image
            className={styles.product_card_icon}
            src={card?.product_card_icon_desktop?.url}
            alt={''}
            width={56}
            height={56}
          />
        </div>

        <div className={styles.product_detail}>
          <div
            className={styles.description_block}
            id={'rich_text_wrapper'}
            dangerouslySetInnerHTML={{
              __html: handleRichText(card?.product_description)
            }}
          ></div>

          {card?.product_brochure_link?.url && (
            <div className={styles.brochure_link}>
              <Image
                src={card?.product_brochure_link?.icon?.url || '/blog/icon/ArrowR.svg'}
                alt={''}
                width={16}
                height={16}
              />
              <a href={card?.product_brochure_link?.url}>{card?.product_brochure_link?.title}</a>
            </div>
          )}
        </div>

        <div className={styles.price_block}>
          {card?.highlight && <div className={styles.highlight}>{card?.highlight}</div>}
          <div className={styles.price_grid}>
            <div className={styles.price_first_column}>
              <div className={styles.grey_font}>低至</div>
              <div className={styles.black_font}>HK$</div>
            </div>
            <div className={styles.price_second_column}>
              <div className={styles.orange_big_price}>{card?.price?.split('.')[0]}</div>
              <div className={styles.orange_small_price}>.{card?.price?.split('.')[1] || '00'}</div>
              <div className={styles.price_unit}>/{card?.price_unit}*</div>
            </div>
            <div className={styles.price_third_column} onClick={() => handleGA(card?.title)}>
              <LinkButton
                hideIcon={true}
                title={card?.action_link?.title}
                targetUrl={`${card?.action_link?.url}`}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const setPagination = (targetIndex: number) => {
    const targetPageIndex = Math.floor(targetIndex / 2) + 1;
    const curPageIndex = Math.floor(activeSwiperIndex / 2) + 1;
    if (targetPageIndex < curPageIndex) {
      swiper.slidePrev();
    }
    if (targetPageIndex > curPageIndex) {
      swiper.slideNext();
    }
    setActiveSwiperIndex(targetIndex);
  };

  const changeSwiperSlide = (e: any) => {
    const curPage = e.activeIndex + 1;
    const targetIndex = curPage * 2 - 1 - 1;
    setActiveSwiperIndex(targetIndex);
  };

  useEffect(() => {
    if (
      window.innerWidth > 1024 &&
      product_cards?.length > 2 &&
      product_block_type === 'product_card'
    ) {
      setSwiperProductCards(true);
      setSwiperProductList(newProductList);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('resize', () => {
      if (
        window.innerWidth > 1024 &&
        product_cards?.length > 2 &&
        product_block_type === 'product_card'
      ) {
        setSwiperProductCards(true);
        setSwiperProductList(newProductList);
      } else {
        setSwiperProductCards(false);
      }
    });
  }, []);

  return (
    cross_sell_title && (
      <div className={styles.cross_sell_block}>
        <div className={styles.title_wrapper}>
          <Image
            className={styles.cross_sell_title_image}
            src={icon?.url}
            alt={''}
            width={24}
            height={24}
          />
          <div className={styles.title}>{cross_sell_title}</div>
        </div>
        {introduction && (
          <div
            className={styles.rich_text}
            id={'rich_text_wrapper'}
            dangerouslySetInnerHTML={{
              __html: handleRichText(introduction)
            }}
          ></div>
        )}

        {isSwiperProductCards && (
          <>
            <Swiper
              className={styles.swiper_block}
              spaceBetween={50}
              onSwiper={swiper => {
                setSwiper(swiper);
              }}
              onSlideChange={e => changeSwiperSlide(e)}
            >
              {swiperProductList.map((twoCardList: [], index) => {
                return (
                  <SwiperSlide key={index} className={styles.swiper_slide}>
                    <div
                      className={classNames(
                        styles.product_list,
                        activeSwiperIndex % 2 === 0 && styles.init_product_list
                      )}
                    >
                      {twoCardList?.map(
                        (item: {
                          product_type: string;
                          product_type_icon: { url: string };
                          title: string;
                          product_name: string;
                          product_card_icon_desktop: { url: string };
                          product_description: string;
                          highlight: string;
                          price: string;
                          price_unit: string;
                          action_link: { title: string; url: string };
                          product_brochure_link: {
                            icon: { url: string };
                            title: string;
                            url: string;
                          };
                        }) => {
                          return ProductCard(item);
                        }
                      )}
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
            <div className={styles.swiper_pagination}>
              {product_cards.map((item: any, index: number) => {
                return (
                  <div
                    key={index}
                    className={
                      activeSwiperIndex === index ? styles.bulletActiveClass : styles.bulletClass
                    }
                    onClick={() => setPagination(index)}
                  ></div>
                );
              })}
            </div>
          </>
        )}
        {!isSwiperProductCards && product_block_type === 'link_only' && (
          <div className={styles.product_link_list}>
            {product_cards?.map((item: { product_name: string; action_link: { url: string } }) => {
              return (
                <div className={styles.product_link} key={item?.product_name}>
                  <a className={styles.title} href={item?.action_link?.url}>
                    <Image src="/blog/icon/ArrowR.svg" alt={''} width={16} height={16} />
                    {item?.product_name}
                  </a>
                </div>
              );
            })}
          </div>
        )}
        {!isSwiperProductCards && product_block_type === 'product_card' && (
          <div className={styles.product_list}>
            {product_cards?.map(
              (item: {
                product_type: string;
                product_type_icon: { url: string };
                title: string;
                product_name: string;
                product_card_icon_desktop: { url: string };
                product_description: string;
                highlight: string;
                price: string;
                price_unit: string;
                action_link: { title: string; url: string };
                product_brochure_link: {
                  icon: { url: string };
                  title: string;
                  url: string;
                };
              }) => {
                return ProductCard(item);
              }
            )}
          </div>
        )}

        {selling_points && (
          <div
            className={styles.rich_text}
            id={'rich_text_wrapper'}
            dangerouslySetInnerHTML={{
              __html: handleRichText(selling_points)
            }}
          ></div>
        )}

        {remark && RemarkBlock()}
      </div>
    )
  );
});
export default CrossSell;
